import { fetchTestExecutionById } from "../../../services/testExecutionAPI";
import { fetchQuestions, fetchQuestionAnswers } from "../../../services/testResultsAPI";

/**
 * Enriches a raw test execution with the questionnaire sections + stored answers
 * and the display fields the report views expect (date, selectedContract).
 * This is the single source of truth for "what a report object looks like",
 * shared by the Testrapporter list and the review pages (used on refresh, when
 * the report is no longer available in router state).
 */
export async function enrichExecution(execution) {
  let questionSections = [];
  // Latest save across all sections — shown to the reviewer so they know how
  // fresh the answers they are reading are.
  let answersUpdatedAt = null;

  try {
    if (execution.contractId) {
      const sections = await fetchQuestions(execution.contractId);

      for (const section of sections) {
        if (execution.keycloakOrgId) {
          const storedAnswers = await fetchQuestionAnswers(
            execution.contractId,
            section.id,
            execution.keycloakOrgId,
            // Answers belong to the tester who submitted the report, not to the
            // reviewer reading it.
            execution.submittedByUserId
          );

          const questionsWithAnswers = [...(section.questions || [])]
            .sort((a, b) => (a.orderIndex ?? 0) - (b.orderIndex ?? 0))
            .map((question) => {
              const answer = storedAnswers?.answers?.find(
                (ans) => ans.questionId === question.id
              );
              return {
                ...question,
                answerId: answer?.id,
                answerBoolean: answer?.answerBoolean,
                answerText: answer?.answerText,
                isUnexpected: answer?.isUnexpected ?? false,
                motivation: answer?.motivation ?? null,
              };
            });

          const sectionUpdatedAt = storedAnswers?.updatedAt ?? null;
          if (sectionUpdatedAt && (!answersUpdatedAt || new Date(sectionUpdatedAt) > new Date(answersUpdatedAt))) {
            answersUpdatedAt = sectionUpdatedAt;
          }

          questionSections.push({
            ...section,
            questions: questionsWithAnswers,
            answersUpdatedAt: sectionUpdatedAt,
          });
        }
      }
    }
  } catch (err) {
    console.error(`Error loading answers for execution ${execution.id}:`, err);
  }

  return {
    ...execution,
    status: execution.status,
    questionSections,
    answersUpdatedAt,
    date: execution.submittedAt || execution.executedAt,
    selectedContract: execution.contractName,
  };
}

/**
 * Rebuilds a single report by id — used by the review pages on a hard refresh,
 * when the enriched report is no longer in router state. Returns null if the
 * execution isn't found (e.g. not accessible to the current user).
 */
export async function loadReportById(executionId) {
  // Fetched by id rather than picked out of an organisation-scoped list: a reviewer's own
  // organisation never holds the submissions they review, so the list would come back empty.
  // The backend applies the same visibility rules as the listing endpoints.
  const execution = await fetchTestExecutionById(executionId);
  if (!execution) return null;
  return enrichExecution(execution);
}
